import type {
  BranchDto,
  CopySaSettingsRequest,
  CopySaSettingsResponse,
  DeviceSaSettings,
  UpdateSaSettingsRequest,
  CreateTenantRequest,
  CreateTenantUserRequest,
  DeviceDetail,
  DeviceDto,
  DeviceWithToken,
  Paged,
  TenantDetail,
  TenantSummary,
  TenantUserDto,
} from '@sellokart/shared';
import { api, queryString } from '@/lib/api';

export interface TenantListParams {
  page: number;
  pageSize: number;
  search: string;
}

/** Fields the SA portal may PATCH on a tenant (derived from the shared DTO). */
export type UpdateTenantInput = Partial<
  Pick<TenantDetail, 'name' | 'phone' | 'address' | 'timezone' | 'currency' | 'status'>
> & {
  /**
   * Not derived from TenantDetail, which carries the RESOLVED plan object
   * while the API takes an id. Null clears the plan; undefined leaves it.
   */
  planId?: string | null;
};

export interface CreateBranchInput {
  name: string;
  address: string;
}

export interface CreateDeviceInput {
  branchId: string;
  label: string;
}

export const tenantKeys = {
  /** Prefix for every tenant query — invalidating this refreshes lists and details. */
  all: ['tenants'] as const,
  list: (params: TenantListParams) => ['tenants', 'list', params] as const,
  detail: (id: string) => ['tenants', 'detail', id] as const,
  /** One till with its licence. Under the tenants prefix so any tenant
      invalidation refreshes an open device page too. */
  device: (deviceId: string) => ['tenants', 'device', deviceId] as const,
  /** One till's SA switches. Under the tenants prefix like `device`. */
  saSettings: (deviceId: string) => ['tenants', 'sa-settings', deviceId] as const,
};

export function fetchTenants(params: TenantListParams, signal?: AbortSignal): Promise<Paged<TenantSummary>> {
  // The API names the name filter `q`; it rejects unknown query params
  // (forbidNonWhitelisted), so sending `search` would 400 rather than degrade.
  const query = queryString({ page: params.page, pageSize: params.pageSize, q: params.search });
  return api.get<Paged<TenantSummary>>(`/admin/tenants${query}`, { signal });
}

export function fetchTenant(id: string, signal?: AbortSignal): Promise<TenantDetail> {
  return api.get<TenantDetail>(`/admin/tenants/${encodeURIComponent(id)}`, { signal });
}

export function createTenant(input: CreateTenantRequest): Promise<TenantDetail> {
  return api.post<TenantDetail>('/admin/tenants', input);
}

export function updateTenant(id: string, input: UpdateTenantInput): Promise<void> {
  return api.patch<void>(`/admin/tenants/${encodeURIComponent(id)}`, input);
}

export function uploadTenantLogo(id: string, file: File): Promise<TenantDetail> {
  const formData = new FormData();
  formData.append('logo', file);
  return api.uploadFile<TenantDetail>(
    `/admin/tenants/${encodeURIComponent(id)}/logo`,
    'PUT',
    formData,
  );
}

export function deleteTenantLogo(id: string): Promise<TenantDetail> {
  return api.delete<TenantDetail>(`/admin/tenants/${encodeURIComponent(id)}/logo`);
}

export function createBranch(tenantId: string, input: CreateBranchInput): Promise<BranchDto> {
  return api.post<BranchDto>(`/admin/tenants/${encodeURIComponent(tenantId)}/branches`, input);
}

/**
 * Registers a till. Resolves to a flat `DeviceDto`: there is no token in the
 * response and no `device` wrapper around it. `POST /license/activate` is the
 * only path that delivers a sync token.
 */
export function createDevice(tenantId: string, input: CreateDeviceInput): Promise<DeviceDto> {
  return api.post<DeviceDto>(`/admin/tenants/${encodeURIComponent(tenantId)}/devices`, input);
}

export function rotateDeviceToken(deviceId: string): Promise<DeviceWithToken> {
  return api.post<DeviceWithToken>(`/admin/devices/${encodeURIComponent(deviceId)}/rotate-token`);
}

export function fetchDeviceDetail(
  deviceId: string,
  signal?: AbortSignal,
): Promise<DeviceDetail> {
  return api.get<DeviceDetail>(`/admin/devices/${encodeURIComponent(deviceId)}`, {
    signal,
  });
}

export function revokeDevice(deviceId: string): Promise<void> {
  return api.post<void>(`/admin/devices/${encodeURIComponent(deviceId)}/revoke`);
}

/**
 * Per-till reporting, within the tenant's entitlement. Switching this on for a
 * tenant that has not bought reporting changes nothing the till can see — the
 * server sends the AND of the two flags.
 */
export function setDeviceReporting(deviceId: string, enabled: boolean): Promise<DeviceDto> {
  return api.post<DeviceDto>(`/admin/devices/${encodeURIComponent(deviceId)}/reporting`, {
    enabled,
  });
}

export function setDeviceSyncInterval(
  deviceId: string,
  intervalSec: number,
): Promise<DeviceDto> {
  return api.post<DeviceDto>(`/admin/devices/${encodeURIComponent(deviceId)}/sync-interval`, {
    intervalSec,
  });
}

/**
 * Adds a portal login to an existing tenant. Repeatable — call it as many
 * times as the tenant needs users, whenever it needs them; a tenant may also
 * have none at all if it only ever wanted a POS licence.
 */
export function addTenantUser(tenantId: string, input: CreateTenantUserRequest): Promise<TenantUserDto> {
  return api.post<TenantUserDto>(`/admin/tenants/${encodeURIComponent(tenantId)}/users`, input);
}

/** SA ▸ whether a portal login sees cost figures (purchase prices, profit, stock value). */
export function updateTenantUser(
  tenantId: string,
  userId: string,
  input: { canViewCost?: boolean },
): Promise<TenantUserDto> {
  return api.patch<TenantUserDto>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`,
    input,
  );
}

export function resetTenantUserPassword(tenantId: string, userId: string, password: string): Promise<void> {
  return api.post<void>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/reset-password`,
    // The API's ResetPasswordDto names it `newPassword`, and the global
    // ValidationPipe rejects unknown fields — `{ password }` was a 400 every time.
    { newPassword: password },
  );
}

export function fetchSaSettings(deviceId: string, signal?: AbortSignal): Promise<DeviceSaSettings> {
  return api.get<DeviceSaSettings>(`/admin/devices/${encodeURIComponent(deviceId)}/sa-settings`, { signal });
}

export function updateSaSettings(deviceId: string, input: UpdateSaSettingsRequest): Promise<DeviceSaSettings> {
  return api.request<DeviceSaSettings>(`/admin/devices/${encodeURIComponent(deviceId)}/sa-settings`, {
    method: 'PUT',
    body: input,
  });
}

export function copySaSettings(deviceId: string, input: CopySaSettingsRequest): Promise<CopySaSettingsResponse> {
  return api.post<CopySaSettingsResponse>(
    `/admin/devices/${encodeURIComponent(deviceId)}/sa-settings/copy`,
    input,
  );
}
